import appSettings from '../appsettings.json';
import { Artist, Genre, Song } from './library-objects';
import { Instrument, PartList } from './music-objects';
import { Project, ProjectSubmission } from './project-objects';
import { Classroom, LiveEvent, ProjectSubmissionToken, User } from './user-objects';

// API object definitions
export interface AccessCodeValidationInfo {
	isValid: boolean;
	accessCode: string;
	classroomId: string;
	classroomName: string;
	isStudentAccessCode: boolean;
	isClassroomAdminAccessCode: boolean;
	isConductionAdminAccessCode: boolean;
	isLimitReached: boolean;
}

export interface ActionResponse {
	success: boolean;
	message: string;
	errorDetails?: string;
}

export interface FileResponse {
	fileBlobUrl: string;
	success: boolean;
	message: string;
}
interface ValidationErrorResponse {
	message: string;
	error: string;
}

export interface SongForm {
	songName: string;
	genreIds: string[];
	artistIds: string[];
}

export interface LiveEventForm {
	name: string,
	startTime: Date | null,
	endTime: Date | null,
	eventAdminLimit: number | null,
	participantLimit: number | null,
	instrumentIds: string[],
	songIds: string[]
}

export interface ClassroomForm {
	name: string,
	studentLimit: number,
	classroomAdminLimit: number,
	defaultInstrumentIds: string[],
	defaultSongIds: string[]
}

export interface SubmissionTokenForm {
	credits: number;
	start: Date;
	expiration: Date;
}

export interface BalancingResult {
	success: boolean | null,
	message: string
};

interface ApiResponse {
	errorMessage?: string;
	errorDetails?: string;
	response?: Response | null;
}

export interface LiveEventProjectLists {
	projectsForReview: Project[];
	projectPerformanceQueue: Project[];
	projectPerformanceQueuePosition: number;
}

export interface UserLiveEvents {
	participatingLiveEvents: LiveEvent[];
	administratingLiveEvents: LiveEvent[];
	playingLiveEvents: LiveEvent[];
}

export interface CurrentTos {
	version: string;
	sha256: string;
}

export interface CreateCreditsCheckoutSessionResponse {
	clientSecret: string;
	purchaseId?: string;
}

export default class ConductionAPI {

	private static async callEndpoint(url: string, method: string, body: any, token?: string): Promise<ApiResponse> {
		try {
			const bodyString = body ? JSON.stringify(body) : undefined;
			const response = await fetch(url, {
				method: method,
				headers: token ? this.getAuthHeaders(token) : { 'Content-Type': 'application/json' },
				body: bodyString
			});

			// If the response is not ok, handle it gracefully
			if (!response.ok) {
				// Log the error details for debugging
				console.error(`Error occurred during ${method} request to ${url}:`, response.status, response.statusText);
				// Return the error without throwing
				return {
					errorMessage: "An error occurred while processing the request.",
					errorDetails: `Error: ${response.status} ${response.statusText}`,
					response: response
				};
			}

			// If the response is ok, return it
			return {
				response: response
			};

		} catch (error) {
			// Fetch throws a TypeError on network failure (e.g. DNS, timeout, blocked)
			if (error instanceof TypeError) {
				// Handle network error
				console.error("Network error:", error.message);

				// Handle network errors or other unexpected errors
				return {
					errorMessage: "A network error occurred. Please check your connection or try again later.",
					errorDetails: `Network error details: ${error.message}`
				};
			}
			// Handle other errors
			console.error("An unexpected error occurred:", error);
			return {
				errorMessage: "An unexpected error occurred.",
			};
		}
	}

	private static getAuthHeaders(token: string, options?: { excludeContentType?: boolean }): Headers {
		if (token) {
			const headers = new Headers();
			headers.append('Authorization', `Bearer ${token}`);
			if (!options?.excludeContentType) {
				headers.append('Content-Type', 'application/json');
			}
			return headers;
		}
		throw Error('Not logged in');
	}

	private static getGuestAuthHeaders(guestToken: string): Headers {
		if (guestToken) {
			const headers = new Headers();
			headers.append('Authorization', `Bearer ${guestToken}`);
			headers.append('Content-Type', 'application/json');
			return headers;
		}
		throw Error('Not in guest mode');
	}

	public static async getCurrentTos(): Promise<CurrentTos | null> {
		try {
			const url = new URL(`tos/current`, appSettings.PerformanceApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' }
			});

			if (!response.ok) {
				// 500 when no current TOS is set; just return null so caller can decide UX.
				console.error('Error retrieving current TOS:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return { version: data.version, sha256: data.sha256 } as CurrentTos;
		} catch (error) {
			console.error('An error occurred while retrieving current TOS:', error);
			return null;
		}
	}

	public static async acceptTos(version: string, token: string, locale?: string): Promise<void> {
		const url = new URL(`tos/accept`, appSettings.PerformanceApiBaseUrl);
		const body = { version, locale };

		const response = await fetch(url.toString(), {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(body)
		});

		if (!response.ok) {
			// Try to surface server message (handles 400 version mismatch, 401 unauthorized, etc.)
			let message = `(${response.status}) ${response.statusText}`;
			try {
				const j = await response.json();
				if (Array.isArray(j?.errors) && j.errors.length) {
					message = j.errors.join(' ');
				} else if (typeof j?.error === 'string') {
					message = j.error;
				} else if (typeof j?.message === 'string') {
					message = j.message;
				}
			} catch {
				// ignore JSON parse error; keep default message
			}
			throw new Error(`Error accepting Terms of Service: ${message}`);
		}
	}

	public static async updatePassword(user: User, newPassword: string, token: string): Promise<void> {
		const url = `${appSettings.ConductionApiBaseUrl}users/${user.id}/password`;

		const endpointCall = await ConductionAPI.callEndpoint(url, 'PUT', newPassword, token);

		if (endpointCall.errorMessage !== undefined) {
			throw new Error("An error occurred while updating the password.");
		}
	}

	public static async getAllSongs(token: string): Promise<Song[] | null> {
		try {
			const url = new URL("all-songs", appSettings.ConductionApiBaseUrl);
			const response = await fetch(url, {
				method: 'get',
				headers: ConductionAPI.getAuthHeaders(token)
			});

			if (!response.ok) {
				console.error('Error retrieving songs:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((song: any) => Song.fromJson(song));
		} catch (error) {
			console.error('An error occurred while retrieving songs:', error);
			return null;
		}
	}

	public static async getAllSongsWithStats(token: string): Promise<Song[] | null> {
		try {
			const url = new URL("all-songs-with-stats", appSettings.ConductionApiBaseUrl);
			const response = await fetch(url, {
				method: 'get',
				headers: ConductionAPI.getAuthHeaders(token)
			});

			if (!response.ok) {
				console.error('Error retrieving songs:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((song: any) => Song.fromJson(song));
		} catch (error) {
			console.error('An error occurred while retrieving songs:', error);
			return null;
		}
	}

	public static async getArtists(): Promise<Artist[] | null> {
		try {
			const response = await fetch(`${appSettings.ConductionApiBaseUrl}artists`);

			if (!response.ok) {
				console.error('Error retrieving artists:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((artist: any) => Artist.fromJson(artist));
		} catch (error) {
			console.error('An error occurred while retrieving genres:', error);
			return null;
		}
	}

	public static async getGenres(): Promise<Genre[] | null> {
		try {
			const response = await fetch(`${appSettings.ConductionApiBaseUrl}genres`);

			if (!response.ok) {
				console.error('Error retrieving genres:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((genre: any) => Genre.fromJson(genre));
		} catch (error) {
			console.error('An error occurred while retrieving genres:', error);
			return null;
		}
	}

	public static async getInstruments(): Promise<Instrument[]> {
		try {
			const url = new URL(`${appSettings.ConductionApiBaseUrl}instruments`);

			const response = await fetch(url, {
				method: 'get',
				//headers: ConductionAPI.getAuthHeaders()
			});

			if (!response.ok) {
				throw new Error(`Error retrieving instruments: ${response.status} ${response.statusText}`);
			}

			const data = await response.json();
			return data.map((instrument: any) => Instrument.fromJson(instrument));
		} catch (error) {
			console.error('An error occurred while retrieving instruments:', error);
			throw error;
		}
	}

	public static async getUser(userId: string, token: string): Promise<User | null> {
		try {
			const url = new URL(`users/${userId}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url, {
				method: 'get',
				headers: ConductionAPI.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving user data:', response.status, response.statusText);
				return null;
			}

			const userData = await response.json();
			return User.fromJson(userData);
		} catch (error) {
			console.error('An error occurred while retrieving user data:', error);
			return null;
		}
	}

	public static async getUserClassroom(userId: string, token: string): Promise<Classroom | null> {
		try {
			const url = new URL(`users/${userId}/classroom`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving user classroom:', response.status, response.statusText);
				throw new Error('Error retrieving user classroom');
			}

			try {
				const data = await response.json();
				return Classroom.fromJson(data);
			} catch (error) {
				return null;
			}
		} catch (error) {
			console.error('An error occurred while retrieving user classroom:', error);
			throw error;
		}
	}

	public static async getUserSongs(token: string): Promise<Song[] | null> {
		try {
			const url = new URL(`${appSettings.ConductionApiBaseUrl}usersongs`);

			const response = await fetch(url.toString(), {
				method: 'get',
				headers: ConductionAPI.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving songs:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((songData: any) => Song.fromJson(songData));
		} catch (error) {
			console.error('An error occurred while retrieving songs:', error);
			return null;
		}
	}

	public static async getGuestSongs(token: string): Promise<Song[] | null> {
		try {
			const url = new URL(`${appSettings.ConductionApiBaseUrl}songs`);

			const response = await fetch(url.toString(), {
				method: 'get',
				headers: ConductionAPI.getGuestAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving songs:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((songData: any) => Song.fromJson(songData));
		} catch (error) {
			console.error('An error occurred while retrieving songs:', error);
			throw error;
		}
	}

	public static async getUnprocessedSongData(songId: string, token: string): Promise<PartList | string> {
		const url = new URL(`${appSettings.PerformanceApiBaseUrl}songs/${songId}/unprocessed`);

		const response = await fetch(url, {
			method: 'get',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			const errorData = (await response.json()) as ValidationErrorResponse;
			return errorData.error || "Unknown error occurred.";
		}

		try {
			const data = await response.json();
			return PartList.fromJson(data);
		} catch (error) {
			console.error("Error parsing response JSON:", error);
			return "Failed to parse song data.";
		}
	}

	public static async getSong(songId: string, token: string): Promise<Song | null> {
		try {
			const url = new URL(`songs/${songId}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving song info:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return Song.fromJson(data);
		} catch (error) {
			console.error('An error occurred while retrieving song info:', error);
			return null;
		}
	}

	public static async getSongData(songId: string): Promise<PartList | null> {
		try {
			const response = await fetch(`${appSettings.ConductionApiBaseUrl}songs/${songId}/data`);

			if (!response.ok) {
				console.error('Error retrieving song data:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			const partList = PartList.fromJson(data);
			partList.setSongId(songId); // This is an important reference when creating timeline items so we can track song usage.
			return partList;
		} catch (error) {
			console.error('An error occurred while retrieving song data:', error);
			return null;
		}
	}

	public static async deleteSong(songId: string, token: string): Promise<void> {
		try {
			const url = new URL(`songs/${songId}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'DELETE',
				headers: ConductionAPI.getAuthHeaders(token)
			});

			if (!response.ok) {
				throw new Error(`Error occurred during deletion: ${response.status} ${response.statusText}`);
			}
		} catch (error: unknown) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error';
			throw new Error(`An error occurred while deleting song: ${errorMessage}`);
		}
	}

	public static async uploadMusicXML(songId: string, file: File, token: string): Promise<void> {
		try {
			const url = new URL(`songs/${songId}/musicxml`, appSettings.PerformanceApiBaseUrl);

			const formData = new FormData();
			formData.append('musicXmlFile', file); // Ensure the field name matches what the server expects

			const response = await fetch(url.toString(), {
				method: 'PUT',
				headers: ConductionAPI.getAuthHeaders(token, { excludeContentType: true }), // Exclude Content-Type
				body: formData,
			});

			if (!response.ok) {
				throw new Error(`Errors found: ${response.status} ${response.statusText}`);
			}
		} catch (error: unknown) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error';
			throw new Error(`An error occurred while uploading musicxml: ${errorMessage}`);
		}
	}

	public static async createNewSongEntry(songForm: SongForm, token: string): Promise<boolean> {
		const url = `${appSettings.ConductionApiBaseUrl}songs`;

		const songFormString = JSON.stringify(songForm);

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: songFormString
		});

		if (!response.ok) {
			throw new Error(`Error adding song: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async logSearchTerm(searchTerm: string, token: string): Promise<void> {
		try {
			const url = new URL(`search-term/${searchTerm}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'POST',
				headers: ConductionAPI.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error sending search terms:', response.status, response.statusText);
			}

			return;
		} catch (error) {
			console.error('An error occurred while searching for songs:', error);
			return;
		}
	}

	public static async processSongData(songId: string, token: string): Promise<void> {
		try {
			const url = new URL(`songs/${songId}/process`, appSettings.PerformanceApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'PUT',
				headers: ConductionAPI.getAuthHeaders(token)
			});

			if (!response.ok) {
				throw new Error(`Errors: ${response.status} ${response.statusText}`);
			}
		} catch (error: unknown) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error';
			throw new Error(`An error occurred while uploading musicxml: ${errorMessage}`);
		}
	}

	public static async setSongGuestAvailability(songId: string, newAvailabilityStatus: boolean, token: string) {
		const url = new URL(`songs/${songId}/guests`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: newAvailabilityStatus ? 'PUT' : 'DELETE',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error setting guest availibility: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async updateSongAssignment(songId: string, classroomIds: string[], token: string) {
		const url = new URL(`songs/${songId}/classroom-assignments`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(classroomIds)
		});

		if (!response.ok) {
			throw new Error(`Error assigning song: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async updateLiveEventInstruments(liveEventId: string, instrumentIds: string[], token: string) {
		const url = new URL(`live-events/${liveEventId}/instruments`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(instrumentIds)
		});

		if (!response.ok) {
			throw new Error(`Error assigning instruments to live event: ${response.status} ${response.statusText}`);
		}

		return true;
	}


	public static async updateLiveEventSongs(liveEventId: string, songIds: string[], token: string) {
		const url = new URL(`live-events/${liveEventId}/songs`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(songIds)
		});

		if (!response.ok) {
			throw new Error(`Error assigning songs to live event: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async updateClassroomSongAssignments(classroomId: string, songIds: string[], token: string) {
		const url = new URL(`classrooms/${classroomId}/default-songs`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(songIds)
		});

		if (!response.ok) {
			throw new Error(`Error assigning songs to classroom: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async getProjects(userId: string, token: string): Promise<Project[] | null> {
		try {
			if (userId == undefined) {
				throw new Error('User ID is not defined');
			}
			const url = new URL(`users/${userId}/projects`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving projects:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((project: any) => Project.fromJson(project));
		} catch (error) {
			console.error('An error occurred while retrieving projects:', error);
			return null;
		}
	}

	public static async createProject(project: Project, token: string): Promise<string | null> {
		try {
			const url = new URL("projects", appSettings.ConductionApiBaseUrl);
			const projectJson = project.toJson();
			const response = await fetch(url, {
				method: 'POST',
				headers: ConductionAPI.getAuthHeaders(token),
				body: JSON.stringify(projectJson)
			});

			if (!response.ok) {
				console.error('Error saving project:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.id;
		} catch (error) {
			console.error('An error occurred while saving the project:', error);
			return null;
		}
	}

	public static async saveProject(project: Project, token: string): Promise<boolean> {
		try {
			const url = `${appSettings.ConductionApiBaseUrl}projects`;
			const projectJson = project.toJson();
			const response = await fetch(url, {
				method: 'PUT',
				headers: ConductionAPI.getAuthHeaders(token),
				body: JSON.stringify(projectJson)
			});

			if (!response.ok) {
				console.error('Error saving project:', response.status, response.statusText);
				return false;
			}
			return true;
		} catch (error) {
			console.error('An error occurred while saving the project:', error);
			return false;
		}
	}

	public static async loadProject(projectId: string, token: string): Promise<Project> {
		try {
			const url = new URL(`${appSettings.ConductionApiBaseUrl}projects/load/${projectId}`);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving project:', response.status, response.statusText);
			}

			const data = await response.json();
			return Project.fromJson(data);
		} catch (error) {
			throw error;
		}
	}

	public static async getClassroomUsers(classroomId: string, token: string): Promise<User[] | null> {
		try {
			const url = new URL(`classrooms/${classroomId}/users`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url, {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving users:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((user: any) => User.fromJson(user));
		} catch (error) {
			console.error('An error occurred while retrieving projects:', error);
			return null;
		}
	}

	public static async getClassroomProjects(classroomId: string, token: string): Promise<Project[] | null> {
		try {
			const url = new URL(`classrooms/${classroomId}/projects`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url, {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving projects:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((project: any) => Project.fromJson(project));
		} catch (error) {
			console.error('An error occurred while retrieving projects:', error);
			return null;
		}
	}

	public static async getClassroomProjectSubmissions(classroomId: string, token: string): Promise<ProjectSubmission[] | null> {
		try {
			const url = new URL(`classrooms/${classroomId}/project-submissions`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url, {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving projects:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((projectSubmission: any) => ProjectSubmission.fromJson(projectSubmission));
		} catch (error) {
			console.error('An error occurred while retrieving projects:', error);
			return null;
		}
	}

	public static async getUserLiveEvents(userId: string, token: string): Promise<UserLiveEvents> {
		try {
			const url = new URL(`users/${userId}/live-events`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving user live events:', response.status, response.statusText);
				throw new Error('Error retrieving user live events');
			}

			const loadLiveEvent = (liveEventJson: any) => {
				const liveEvent = LiveEvent.fromJson(liveEventJson);
				liveEvent.loadEventAdmins(liveEventJson.eventAdmins?.map((admin: any) => User.fromJson(admin)));
				liveEvent.loadParticipants(liveEventJson.participants?.map((participant: any) => User.fromJson(participant)));
				liveEvent.loadInstrumentalists(liveEventJson.instrumentalists?.map((instrumentalist: any) => User.fromJson(instrumentalist)));
				liveEvent.loadProjectLists(
					liveEventJson.projectsForReview?.map((project: any) => Project.fromJson(project)),
					liveEventJson.projectPerformanceQueue?.map((project: any) => Project.fromJson(project)),
					liveEventJson.projectPerformanceQueuePosition
				);
				return liveEvent;
			};

			const data = await response.json();
			const userLiveEvents: UserLiveEvents = {
				participatingLiveEvents: data.participatingLiveEvents.map((liveEventJson: any) => loadLiveEvent(liveEventJson)),
				administratingLiveEvents: data.administratingLiveEvents.map((liveEventJson: any) => loadLiveEvent(liveEventJson)),
				playingLiveEvents: data.playingLiveEvents.map((liveEvent: any) => loadLiveEvent(liveEvent)),
			};
			return userLiveEvents;
		} catch (error) {
			console.error('An error occurred while retrieving user live events:', error);
			throw error;
		}
	}

	public static async getAllLiveEvents(token: string): Promise<LiveEvent[] | null> {
		try {
			const url = new URL("live-events", appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving live events:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((liveEvent: any) => {
				const loadedLiveEvent = LiveEvent.fromJson(liveEvent);
				loadedLiveEvent.loadEventAdmins(liveEvent.eventAdmins?.map((admin: any) => User.fromJson(admin)));
				loadedLiveEvent.loadParticipants(liveEvent.participants?.map((participant: any) => User.fromJson(participant)));
				loadedLiveEvent.loadInstrumentalists(liveEvent.instrumentalists?.map((instrumentalist: any) => User.fromJson(instrumentalist)));
				loadedLiveEvent.loadProjectLists(
					liveEvent.projectsForReview?.map((project: any) => Project.fromJson(project)),
					liveEvent.projectPerformanceQueue?.map((project: any) => Project.fromJson(project)),
					liveEvent.projectPerformanceQueuePosition
				);
				loadedLiveEvent.loadInstruments(liveEvent.instruments?.map((instrument: any) => Instrument.fromJson(instrument)));
				return loadedLiveEvent;
			});
		} catch (error) {
			console.error('An error occurred while retrieving classrooms:', error);
			return null;
		}
	}

	public static async getAllClassrooms(token: string): Promise<Classroom[] | null> {
		try {
			const url = new URL("classrooms", appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving classrooms:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((classroom: any) => {
				const loadedClassroom = Classroom.fromJson(classroom);
				loadedClassroom.loadClassroomAdmins(classroom.classroomAdmins?.map((admin: any) => User.fromJson(admin)));
				loadedClassroom.loadClassroomStudents(classroom.students?.map((student: any) => User.fromJson(student)));
				return loadedClassroom;
			});
		} catch (error) {
			console.error('An error occurred while retrieving classrooms:', error);
			return null;
		}
	}

	public static async submitProjectForLiveEvent(liveEventId: string, projectId: string, token: string): Promise<LiveEventProjectLists> {
		try {
			const url = new URL(`live-events/${liveEventId}/submit/${projectId}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'PUT',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error submitting project for live event:', response.status, response.statusText);
				throw new Error('Error submitting project for live event');
			}

			const data = await response.json();
			const projectsForReview: Project[] = data.projectsForReview?.map((project: any) => Project.fromJson(project));
			const projectPerformanceQueue: Project[] = data.projectPerformanceQueue?.map((project: any) => Project.fromJson(project));
			const projectPerformanceQueuePosition: number = data.projectPerformanceQueuePosition;
			const projectLists: LiveEventProjectLists = {
				projectsForReview: projectsForReview || [],
				projectPerformanceQueue: projectPerformanceQueue || [],
				projectPerformanceQueuePosition: projectPerformanceQueuePosition || 0,
			};
			return projectLists;
		} catch (error) {
			console.error('An error occurred while submitting project for live event:', error);
			throw error;
		}
	}

	public static async approveLiveEventProject(liveEventId: string, projectId: string, token: string): Promise<LiveEventProjectLists> {
		try {
			const url = new URL(`live-events/${liveEventId}/approve/${projectId}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'PUT',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error approving project:', response.status, response.statusText);
				throw new Error('Error approving project');
			}
			const data = await response.json();
			const projectsForReview: Project[] = data.projectsForReview?.map((project: any) => Project.fromJson(project));
			const projectPerformanceQueue: Project[] = data.projectPerformanceQueue?.map((project: any) => Project.fromJson(project));
			const projectPerformanceQueuePosition: number = data.projectPerformanceQueuePosition;
			const projectLists: LiveEventProjectLists = {
				projectsForReview: projectsForReview || [],
				projectPerformanceQueue: projectPerformanceQueue || [],
				projectPerformanceQueuePosition: projectPerformanceQueuePosition || 0,
			};
			return projectLists;
		} catch (error) {
			console.error('An error occurred while approving project:', error);
			throw error;
		}
	}

	public static async removeProjectFromLiveEvent(liveEventId: string, projectId: string, token: string): Promise<LiveEventProjectLists> {
		try {
			const url = new URL(`live-events/${liveEventId}/remove/${projectId}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'PUT',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error removing project from live event:', response.status, response.statusText);
				throw new Error('Error removing project from live event');
			}
			const data = await response.json();
			const projectsForReview: Project[] = data.projectsForReview?.map((project: any) => Project.fromJson(project));
			const projectPerformanceQueue: Project[] = data.projectPerformanceQueue?.map((project: any) => Project.fromJson(project));
			const projectPerformanceQueuePosition: number = data.projectPerformanceQueuePosition;
			const projectLists: LiveEventProjectLists = {
				projectsForReview: projectsForReview || [],
				projectPerformanceQueue: projectPerformanceQueue || [],
				projectPerformanceQueuePosition: projectPerformanceQueuePosition || 0,
			};
			return projectLists;
		} catch (error) {
			console.error('An error occurred while removing project from live event:', error);
			throw error;
		}
	}

	public static async removeProjectFromQueue(liveEventId: string, projectId: string, token: string): Promise<LiveEventProjectLists> {
		try {
			const url = new URL(`live-events/${liveEventId}/unqueue/${projectId}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'PUT',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error removing project from queue:', response.status, response.statusText);
				throw new Error('Error removing project from queue');
			}

			const data = await response.json();
			const projectsForReview: Project[] = data.projectsForReview?.map((project: any) => Project.fromJson(project));
			const projectPerformanceQueue: Project[] = data.projectPerformanceQueue?.map((project: any) => Project.fromJson(project));
			const projectPerformanceQueuePosition: number = data.projectPerformanceQueuePosition;
			const projectLists: LiveEventProjectLists = {
				projectsForReview: projectsForReview || [],
				projectPerformanceQueue: projectPerformanceQueue || [],
				projectPerformanceQueuePosition: projectPerformanceQueuePosition || 0,
			};
			return projectLists;
		} catch (error) {
			console.error('An error occurred while removing project from queue:', error);
			throw error;
		}
	}

	public static async updateLiveEventQueuePosition(liveEventId: string, newPosition: number, token: string): Promise<LiveEventProjectLists> {
		try {
			const url = new URL(`live-events/${liveEventId}/queue-position/${newPosition}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'PUT',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error changing live event queue position:', response.status, response.statusText);
				throw new Error('Error changing live event queue position');
			}

			const data = await response.json();
			const projectsForReview: Project[] = data.projectsForReview?.map((project: any) => Project.fromJson(project));
			const projectPerformanceQueue: Project[] = data.projectPerformanceQueue?.map((project: any) => Project.fromJson(project));
			const projectPerformanceQueuePosition: number = data.projectPerformanceQueuePosition;
			const projectLists: LiveEventProjectLists = {
				projectsForReview: projectsForReview || [],
				projectPerformanceQueue: projectPerformanceQueue || [],
				projectPerformanceQueuePosition: projectPerformanceQueuePosition || 0,
			};
			return projectLists;
		} catch (error) {
			console.error('An error occurred while changing live event queue position:', error);
			throw error;
		}
	}

	public static async getLiveEventSongs(liveEventId: string, token: string): Promise<Song[]> {
		try {
			const url = new URL(`live-events/${liveEventId}/songs`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving live event songs:', response.status, response.statusText);
				throw new Error('Error retrieving live event songs');
			}

			const data = await response.json();
			return data.map((song: any) => Song.fromJson(song));
		} catch (error) {
			console.error('An error occurred while retrieving live event songs:', error);
			throw new Error('An error occurred while retrieving live event songs');
		}
	}

	public static async getLiveEventInstruments(liveEventId: string, token: string): Promise<Instrument[]> {
		try {
			const url = new URL(`live-events/${liveEventId}/instruments`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving live event instruments:', response.status, response.statusText);
				throw new Error('Error retrieving live event instruments');
			}

			const data = await response.json();
			return data.map((instrument: any) => Instrument.fromJson(instrument));
		} catch (error) {
			console.error('An error occurred while retrieving live event instruments:', error);
			throw new Error('An error occurred while retrieving live event instruments');
		}
	}

	public static async getLiveEventProjectLists(liveEventId: string, token: string): Promise<LiveEventProjectLists> {
		try {
			const url = new URL(`live-events/${liveEventId}/project-lists`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving live event project list info:', response.status, response.statusText);
				throw new Error('Error retrieving live event project list info');
			}

			const data = await response.json();
			const projectsForReview: Project[] = data.projectsForReview?.map((project: any) => Project.fromJson(project));
			const projectPerformanceQueue: Project[] = data.projectPerformanceQueue?.map((project: any) => Project.fromJson(project));
			const projectPerformanceQueuePosition: number = data.projectPerformanceQueuePosition;
			const projectLists: LiveEventProjectLists = {
				projectsForReview: projectsForReview || [],
				projectPerformanceQueue: projectPerformanceQueue || [],
				projectPerformanceQueuePosition: projectPerformanceQueuePosition || 0,
			};
			return projectLists;
		} catch (error) {
			console.error('An error occurred while retrieving live event info:', error);
			throw error;
		}
	}

	public static async getLiveEvent(liveEventId: string, token: string): Promise<LiveEvent | null> {
		try {
			const url = new URL(`live-events/${liveEventId}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving live event info:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			const loadedLiveEvent = LiveEvent.fromJson(data);
			loadedLiveEvent.loadEventAdmins(data.eventAdmins?.map((admin: any) => User.fromJson(admin)));
			loadedLiveEvent.loadParticipants(data.participants?.map((participant: any) => User.fromJson(participant)));
			loadedLiveEvent.loadInstrumentalists(data.instrumentalists?.map((instrumentalist: any) => User.fromJson(instrumentalist)));
			loadedLiveEvent.loadProjectLists(
				data.projectsForReview?.map((project: any) => Project.fromJson(project)),
				data.projectPerformanceQueue?.map((project: any) => Project.fromJson(project)),
				data.projectPerformanceQueuePosition
			);
			loadedLiveEvent.loadInstruments(data.instruments?.map((instrument: any) => Instrument.fromJson(instrument)));
			return loadedLiveEvent;
		} catch (error) {
			console.error('An error occurred while retrieving live event info:', error);
			return null;
		}
	}

	public static async getClassroom(classroomId: string, token: string): Promise<Classroom | null> {
		try {
			const url = new URL(`classrooms/${classroomId}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving classroom info:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return Classroom.fromJson(data);
		} catch (error) {
			console.error('An error occurred while retrieving classroom info:', error);
			return null;
		}
	}

	public static async deleteLiveEvent(liveEventId: string, token: string): Promise<void> {
		try {
			const url = new URL(`live-events/${liveEventId}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'DELETE',
				headers: ConductionAPI.getAuthHeaders(token)
			});

			if (!response.ok) {
				throw new Error(`Error occurred during live event deletion: ${response.status} ${response.statusText}`);
			}
		} catch (error: unknown) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error';
			throw new Error(`An error occurred while deleting live event: ${errorMessage}`);
		}
	}

	public static async getClassroomInstruments(classroomId: string, token: string): Promise<Instrument[]> {
		try {
			const url = new URL(`classrooms/${classroomId}/instruments`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving classroom instruments:', response.status, response.statusText);
				throw new Error('Error retrieving classroom instruments');
			}

			const data = await response.json();
			return data.map((instrument: any) => Instrument.fromJson(instrument));
		} catch (error) {
			console.error('An error occurred while retrieving classroom instruments:', error);
			throw error;
		}
	}

	public static async getClassroomSongs(classroomId: string, token: string): Promise<Song[]> {
		try {
			const url = new URL(`classrooms/${classroomId}/songs`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving classroom songs:', response.status, response.statusText);
				throw new Error('Error retrieving classroom songs');
			}

			const data = await response.json();
			return data.map((song: any) => Song.fromJson(song));
		} catch (error) {
			console.error('An error occurred while retrieving classroom songs:', error);
			throw error;
		}
	}

	public static async deleteClassroom(classroomId: string, token: string): Promise<void> {
		try {
			const url = new URL(`classrooms/${classroomId}`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'DELETE',
				headers: ConductionAPI.getAuthHeaders(token)
			});

			if (!response.ok) {
				throw new Error(`Error occurred during classroom deletion: ${response.status} ${response.statusText}`);
			}
		} catch (error: unknown) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error';
			throw new Error(`An error occurred while deleting classroom: ${errorMessage}`);
		}
	}

	public static async createLiveEvent(liveEventForm: LiveEventForm, token: string) {
		const url = new URL(`live-events`, appSettings.ConductionApiBaseUrl);

		const liveEventFormString = JSON.stringify(liveEventForm);

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: liveEventFormString
		});

		if (!response.ok) {
			throw new Error(`Error creating live event: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async createClassroom(classroomForm: ClassroomForm, token: string) {
		const url = new URL(`classrooms`, appSettings.ConductionApiBaseUrl);

		const classroomFormString = JSON.stringify(classroomForm);

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: classroomFormString
		});

		if (!response.ok) {
			throw new Error(`Error creating classroom: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async duplicateClassroom(classroomId: string, newClassroomName: string, token: string) {
		const url = new URL(`classrooms/${classroomId}/duplicate/${newClassroomName}`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error duplicating classroom: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async renameLiveEvent(eventId: string, newName: string, token: string) {
		const url = new URL(`live-events/${eventId}/rename/${newName}`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error updating event name: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async renameClassroom(classroomId: string, newName: string, token: string) {
		const url = new URL(`classrooms/${classroomId}/rename/${newName}`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error updating classroom name: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async setLiveEventParticipantLimit(eventId: string, newLimit: number | null, token: string) {
		const url = new URL(`live-events/${eventId}/participant-limit/${newLimit ?? ""}`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error updating live event participant limit: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async setLiveEventAdminLimit(eventId: string, newLimit: number | null, token: string) {
		const url = new URL(`live-events/${eventId}/event-admin-limit/${newLimit ?? ""}`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error updating live event admin limit: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async setClassroomAdminLimit(classroomId: string, newLimit: number, token: string) {
		const url = new URL(`classrooms/${classroomId}/classroom-admin-limit/${newLimit}`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error updating classroom admin limit: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async setStudentLimit(classroomId: string, newLimit: number, token: string) {
		const url = new URL(`classrooms/${classroomId}/student-limit/${newLimit}`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error updating student limit: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async getSubmissionTokens(classroomId: string, token: string): Promise<ProjectSubmissionToken[] | null> {
		try {
			const url = new URL(`classrooms/${classroomId}/submission-credits`, appSettings.ConductionApiBaseUrl);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving classroom submission credits:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((tokenJson: any) => ProjectSubmissionToken.fromJson(tokenJson));
		} catch (error) {
			console.error('An error occurred while retrieving classroom submission credits:', error);
			return null;
		}
	}

	public static async addSubmissionToken(classroomId: string, tokenForm: SubmissionTokenForm, token: string) {
		const url = new URL(`classrooms/${classroomId}/submission-credits`, appSettings.ConductionApiBaseUrl);

		const tokenFormString = JSON.stringify(tokenForm);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token),
			body: tokenFormString
		});

		if (!response.ok) {
			throw new Error(`Error adding credits to classroom: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async removeSubmissionToken(classroomId: string, tokenId: string, token: string) {
		const url = new URL(`classrooms/${classroomId}/submission-credits/${tokenId}`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'DELETE',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error adding credits to classroom: ${response.status} ${response.statusText}`);
		}

		return true;
	}

	public static async balanceSubmissionTokens(classroomId: string, token: string): Promise<BalancingResult[]> {
		const url = new URL(`classrooms/${classroomId}/submission-credits`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PATCH',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error balancing credits to classroom: ${response.status} ${response.statusText}`);
		}

		return await response.json();
	}

	public static async getProjectSubmissions(token: string): Promise<ProjectSubmission[] | null> {
		try {
			const url = new URL(`${appSettings.ConductionApiBaseUrl}submissions`);

			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: this.getAuthHeaders(token),
			});

			if (!response.ok) {
				console.error('Error retrieving projects:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data.map((submission: any) => ProjectSubmission.fromJson(submission));
		} catch (error) {
			console.error('An error occurred while retrieving projects:', error);
			return null;
		}
	}

	public static async prepareProjectInstrumentParts(projectId: string, token: string): Promise<void> {
		const url = new URL(`prepare-parts/${projectId}`, appSettings.PerformanceApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error preparing project instrument parts: ${response.status} ${response.statusText}`);
		}

		return;
	}

	public static async reportDrag(songId: string, measureCount: number, partName: string, sectionName: string, token: string): Promise<void> {
		const url = new URL(`songs/${songId}/report-drag`, appSettings.ConductionApiBaseUrl);
		const body = JSON.stringify({
			measureCount: measureCount,
			partName: partName,
			sectionName: sectionName
		});

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body
		});

		if (!response.ok) {
			throw new Error(`Error reporting drag for song: ${response.status} ${response.statusText}`);
		}
	}

	public static async reportPreview(songId: string, partName: string, sectionName: string, token: string): Promise<void> {
		const url = new URL(`songs/${songId}/report-preview`, appSettings.ConductionApiBaseUrl);
		const body = JSON.stringify({
			partName: partName,
			sectionName: sectionName
		});

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body
		});

		if (!response.ok) {
			throw new Error(`Error reporting preview for song: ${response.status} ${response.statusText}`);
		}
	}

	// This is a more efficient way to get the project instrument parts as a zip file.
	// It is a single endpoint that returns a zip file containing all the parts for the project.
	// The endpoint has all of the PDFs pre-processed and ready to go, so it is much faster and more efficient than the export method.
	public static async getProjectInstrumentPartPdfs(projectId: string, token: string): Promise<FileResponse> {
		const url = new URL(`get-parts/${projectId}`, appSettings.PerformanceApiBaseUrl);

		const response = await fetch(url, {
			method: 'GET',
			headers: ConductionAPI.getAuthHeaders(token)
		});


		let fileResponse: FileResponse;
		if (response.ok) {
			const zipBlib = await response.blob();
			const blobUrl = window.URL.createObjectURL(zipBlib);
			fileResponse = {
				fileBlobUrl: blobUrl,
				success: true,
				message: ''
			};
		} else {
			console.error(`Failed to download ZIP.`);
			fileResponse = {
				fileBlobUrl: '',
				success: false,
				message: `An error occurred while getting project instruments zip: (${response.status}) ${response.statusText}`
			};
		}

		return fileResponse;
	}

	public static async exportProjectToMusicXml(project: Project, token: string): Promise<FileResponse> {
		const url = `${appSettings.PerformanceApiBaseUrl}export/musicxml`;

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(project.toJson())
		});

		let fileResponse: FileResponse;
		if (response.ok) {
			const musicXmlBlob = await response.blob();
			const blobUrl = window.URL.createObjectURL(musicXmlBlob);
			fileResponse = {
				fileBlobUrl: blobUrl,
				success: true,
				message: ''
			};
		} else {
			console.error(`Failed to download MusicXml.`);
			fileResponse = {
				fileBlobUrl: '',
				success: false,
				message: `An error occurred while exporting this project: (${response.status}) ${response.statusText}`
			};
		}

		return fileResponse;
	}

	public static async exportProjectToMp3(project: Project, token: string): Promise<FileResponse> {
		const url = `${appSettings.PerformanceApiBaseUrl}export/mp3`;

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(project.toJson())
		});

		let fileResponse: FileResponse;
		if (response.ok) {
			const mp3Blob = await response.blob();
			const blobUrl = window.URL.createObjectURL(mp3Blob);
			fileResponse = {
				fileBlobUrl: blobUrl,
				success: true,
				message: ''
			};
		} else {
			console.error(`Failed to download MP3.`);
			fileResponse = {
				fileBlobUrl: '',
				success: false,
				message: `An error occurred while exporting this project: (${response.status}) ${response.statusText}`
			};
		}

		return fileResponse;
	}

	public static async exportProjectToPdf(project: Project, token: string): Promise<FileResponse> {
		const url = `${appSettings.PerformanceApiBaseUrl}export/pdf`;

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(project.toJson())
		});

		let fileResponse: FileResponse;
		if (response.ok) {
			const pdfBlob = await response.blob();
			const blobUrl = window.URL.createObjectURL(pdfBlob);
			fileResponse = {
				fileBlobUrl: blobUrl,
				success: true,
				message: ''
			};
		} else {
			console.error(`Failed to download PDF.`);
			fileResponse = {
				fileBlobUrl: '',
				success: false,
				message: `An error occurred while exporting this project: (${response.status}) ${response.statusText}`
			};
		}

		return fileResponse;
	}

	public static async exportProjectToZip(project: Project, token: string): Promise<FileResponse> {
		const url = `${appSettings.PerformanceApiBaseUrl}export/zip`;

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(project.toJson())
		});

		let fileResponse: FileResponse;
		if (response.ok) {
			const zipBlib = await response.blob();
			const blobUrl = window.URL.createObjectURL(zipBlib);
			fileResponse = {
				fileBlobUrl: blobUrl,
				success: true,
				message: ''
			};
		} else {
			console.error(`Failed to download ZIP.`);
			fileResponse = {
				fileBlobUrl: '',
				success: false,
				message: `An error occurred while exporting this project: (${response.status}) ${response.statusText}`
			};
		}

		return fileResponse;
	}

	public static async publishProject(project: Project, token: string): Promise<ActionResponse> {
		const url = `${appSettings.PerformanceApiBaseUrl}publish`;

		const response = await fetch(url, {
			method: 'POST',
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify(project.toJson())
		});

		const message = response ? await response.text() : "";
		if (!response.ok) {
			return {
				success: false,
				message: message
			};
		}

		return {
			success: true,
			message: message
		};
	}

	public static async createSubmissionCreditsCheckoutSession(
		bundleId: string,
		token: string
	): Promise<CreateCreditsCheckoutSessionResponse> {
		const url = new URL(
			`billing/submission-credits/checkout-session`,
			appSettings.PerformanceApiBaseUrl
		);

		const response = await fetch(url.toString(), {
			method: "POST",
			headers: ConductionAPI.getAuthHeaders(token),
			body: JSON.stringify({ bundleId })
		});

		if (!response.ok) throw new Error(`(${response.status}) ${response.statusText}`);

		const data: any = await response.json();

		const clientSecret: string | undefined = data?.clientSecret ?? data?.ClientSecret;
		const purchaseId: string | undefined = data?.purchaseId ?? data?.PurchaseId;

		if (clientSecret && purchaseId) return { clientSecret, purchaseId };

		throw new Error("Invalid response from server.");
	}

	public static async getMyProjectSubmissionTokens(token: string): Promise<ProjectSubmissionToken[]> {
		const url = new URL("submission-tokens", appSettings.ConductionApiBaseUrl);
		try {
			const response = await fetch(url.toString(), {
				method: 'GET',
				headers: ConductionAPI.getAuthHeaders(token),
			});
			if (!response.ok) {
				throw new Error(`Error retrieving my submission tokens: ${response.status} ${response.statusText}`);
			}
			const data = await response.json();
			return data.map((tokenJson: any) => ProjectSubmissionToken.fromJson(tokenJson));
		} catch (error) {
			console.error('An error occurred while retrieving my submission tokens:', error);
			throw error;
		}
	}

	public static async sendToMusicians(projectId: string, username: string, password: string, isClassroomSubmission: boolean, token: string): Promise<ActionResponse> {
		const url = `${appSettings.PerformanceApiBaseUrl}submit`;

		const payload = {
			projectId,
			username,
			password,
			isClassroomSubmission
		};

		const endpointCall = await ConductionAPI.callEndpoint(url, 'PUT', payload, token);

		const message = endpointCall && endpointCall.response ? await endpointCall.response.text() : "";

		if (endpointCall.response && !endpointCall.response.ok) {
			return {
				success: false,
				message: message
			};
		}

		if (endpointCall.errorMessage != undefined) {
			return {
				success: false,
				message: endpointCall.errorMessage,
				errorDetails: endpointCall.errorDetails
			};
		}

		return {
			success: true,
			message: message
		};
	}

	public static async setTutorialCompletionStatus(userId: string, completionStatus: boolean, token: string) {
		const url = new URL(`users/${userId}/beginner-tutorial/${completionStatus}`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token)
		});

		if (!response.ok) {
			throw new Error(`Error setting tutorial competion status: ${response.status} ${response.statusText}`);
		}

		return;
	}

	public static async getLiveEventByCode(accessCode: string): Promise<LiveEvent | null> {
		try {
			const response = await fetch(`${appSettings.ConductionApiBaseUrl}live-events/code/${accessCode}`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json'
				}
			});

			if (!response.ok) {
				return null;
			}

			const data = await response.json();
			const loadedLiveEvent = LiveEvent.fromJson(data);
			loadedLiveEvent.loadEventAdmins(data.eventAdmins?.map((admin: any) => User.fromJson(admin)));
			loadedLiveEvent.loadParticipants(data.participants?.map((participant: any) => User.fromJson(participant)));
			loadedLiveEvent.loadInstrumentalists(data.instrumentalists?.map((instrumentalist: any) => User.fromJson(instrumentalist)));
			loadedLiveEvent.loadProjectLists(
				data.projectsForReview?.map((project: any) => Project.fromJson(project)),
				data.projectPerformanceQueue?.map((project: any) => Project.fromJson(project)),
				data.projectPerformanceQueuePosition
			);
			loadedLiveEvent.loadInstruments(data.instruments?.map((instrument: any) => Instrument.fromJson(instrument)));
			return loadedLiveEvent;
		} catch (error) {
			console.error('Error validating access code:', error);
			throw error;
		}
	}

	public static async validateClassroomAccessCode(accessCode: string): Promise<AccessCodeValidationInfo> {
		try {
			const response = await fetch(`${appSettings.ConductionApiBaseUrl}classrooms/${accessCode}/validate`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json'
				}
			});

			if (!response.ok) {
				return {
					isValid: false,
					accessCode: accessCode,
					classroomId: "",
					classroomName: "",
					isStudentAccessCode: false,
					isClassroomAdminAccessCode: false,
					isConductionAdminAccessCode: false,
					isLimitReached: false
				};
			}

			const data: AccessCodeValidationInfo = await response.json();
			return {
				isValid: true,
				accessCode: data.accessCode,
				classroomId: data.classroomId,
				classroomName: data.classroomName,
				isStudentAccessCode: data.isStudentAccessCode,
				isClassroomAdminAccessCode: data.isClassroomAdminAccessCode,
				isConductionAdminAccessCode: data.isConductionAdminAccessCode,
				isLimitReached: data.isLimitReached
			};
		} catch (error) {
			console.error('Error validating access code:', error);
			throw error;
		}
	}

	public static async signUpForLiveEvent(accessCode: string, token: string): Promise<boolean> {
		const url = new URL(`live-events/sign-up/${accessCode}`, appSettings.ConductionApiBaseUrl);

		const response = await fetch(url, {
			method: 'PUT',
			headers: ConductionAPI.getAuthHeaders(token),
		});

		if (response.ok) {
			return true;
		} else {
			const errorMessage = await response.text();
			console.error(`Error signing up for live event: ${response.status} ${response.statusText} - ${errorMessage}`);
		}

		return false;
	}

	public static async registerUser(username: string, email: string, firstName: string, lastName: string, password: string, confirmPassword: string): Promise<boolean> {
		const url = `${appSettings.ConductionApiBaseUrl}register/user`;
		const payload = {
			username,
			email,
			firstName,
			lastName,
			password,
			confirmPassword,
		};

		try {
			const endpointCall = await ConductionAPI.callEndpoint(url, 'POST', payload);

			if (endpointCall.response && !endpointCall.response.ok) {
				console.error(await endpointCall.response.json());
				return false;
			}

			if (endpointCall.errorMessage != undefined) {
				return false;
			}

			return true;
		} catch {
			return false;
		}
	}

	public static async registerLiveEventUser(username: string, liveEventAccessCode: string, email: string, password: string, confirmPassword: string): Promise<boolean> {
		const url = `${appSettings.ConductionApiBaseUrl}register/live-event-user`;
		const payload = {
			username,
			liveEventAccessCode,
			email,
			password,
			confirmPassword,
		};

		const endpointCall = await ConductionAPI.callEndpoint(url, 'POST', payload);

		if (endpointCall.response && !endpointCall.response.ok) {
			console.error(await endpointCall.response.json());
			return false;
		}

		if (endpointCall.errorMessage != undefined) {
			return false;
		}

		return true;
	}

	public static async registerStudent(username: string, studentAccessCode: string, password: string, confirmPassword: string): Promise<boolean> {
		const url = `${appSettings.ConductionApiBaseUrl}register/student`;
		const payload = {
			username,
			studentAccessCode,
			password,
			confirmPassword,
		};

		const endpointCall = await ConductionAPI.callEndpoint(url, 'POST', payload);

		if (endpointCall.response && !endpointCall.response.ok) {
			console.error(await endpointCall.response.json());
			return false;
		}

		if (endpointCall.errorMessage != undefined) {
			return false;
		}

		return true;
	}

	public static async registerClassroomAdmin(username: string, classroomAdminAccessCode: string, email: string, password: string, confirmPassword: string): Promise<boolean> {
		const url = `${appSettings.ConductionApiBaseUrl}register/classroomadmin`;
		const payload = {
			username,
			classroomAdminAccessCode,
			email,
			password,
			confirmPassword,
		};

		const endpointCall = await ConductionAPI.callEndpoint(url, 'POST', payload);

		if (endpointCall.response && !endpointCall.response.ok) {
			console.error(await endpointCall.response.json());
			return false;
		}

		if (endpointCall.errorMessage != undefined) {
			return false;
		}

		return true;
	}

	public static async registerConductionAdmin(username: string, conductionAdminAccessCode: string, email: string, password: string, confirmPassword: string): Promise<boolean> {
		const url = `${appSettings.ConductionApiBaseUrl}register/conductionadmin`;
		const payload = {
			username,
			conductionAdminAccessCode,
			email,
			password,
			confirmPassword,
		};

		const endpointCall = await ConductionAPI.callEndpoint(url, 'POST', payload);

		if (endpointCall.response && !endpointCall.response.ok) {
			console.error(await endpointCall.response.json());
			return false;
		}

		if (endpointCall.errorMessage != undefined) {
			return false;
		}

		return true;
	}

	public static async guestLogin(): Promise<{ user: User, token: string } | null> {
		const url = `${appSettings.ConductionApiBaseUrl}login/guest`;

		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			}
		});

		if (response.ok) {
			const data = await response.json();
			const user = User.getGuestUser();
			const token = data.token;

			return { user, token };
		} else {
			if (response.status != 401) {
				throw new Error(`The API responded with ${response.status}: ${response.statusText}`);
			}
		}

		return null;
	}

	public static async login(usernameOrEmail: string, password: string, rememberMe: boolean = false): Promise<{ user: User, token: string } | null> {
		const url = new URL("login", appSettings.ConductionApiBaseUrl);
		const payload = {
			usernameOrEmail,
			password,
			rememberMe
		};

		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		});

		if (response.ok) {
			// Store the JWT token and userId from the response
			const data = await response.json();
			const token = data.token;
			const user = User.fromJson(data.user);
			return { user, token }
		} else {
			if (response.status != 401) {
				throw new Error(`The API responded with ${response.status}: ${response.statusText}`);
			}
		}
		return null;
	}
}
